// Ultimate Card Game (UCG) — AI Synergy Matrix (Data only)
// Automatically generated by: npm run ai:eval -- mine-combos [--delta] --save
//
// Static causal synergy scores mined via 4-world counterfactual evaluation.
// Do not edit manually; re-mine with --delta on balance or card changes.

export const VALID_CONTEXT_TAGS = Object.freeze([
  'ANY',
  'SPAWN_SWARM',
  'REQ_ENEMY_GROUND_CLUSTER',
  'REQ_ENEMY_AT_FRONTLINE',
  'REQ_STABLE_FRONTLINE',
  'REQ_PUSH_ADVANTAGE',
  'REQ_ENGAGED_SWARM'
]);

export const VIRTUAL_TRIGGER_NODES = Object.freeze([
  'MODULE:capsule_sling_ready',
  'STATE:nuke_imminent'
]);

export function computeCardSynergyHash(card, hint = null) {
  const canonical = {
    id: card.id,
    name: card.name,
    race: card.race,
    type: card.type || 'Unit',
    unitType: card.unitType || null,
    cost: Number(card.cost || 0),
    hp: Number(card.hp || 0),
    damage: Number(card.damage || 0),
    attackSpeed: Number(card.attackSpeed || 0),
    range: Number(card.range || 0),
    speed: Number(card.speed || 0),
    accuracy: Number(card.accuracy || 0),
    deflectionRating: Number(card.deflectionRating || 0),
    splashRadius: Number(card.splashRadius || 0),
    reloadSkipCount: Number(card.reloadSkipCount || 0),
    projectileType: card.projectileType || null,
    elementType: card.elementType || null,
    targetsAir: card.targetsAir !== false,
    targetsGround: card.targetsGround !== false,
    deflectionScaling: Boolean(card.deflectionScaling),
    reflectDamageRatio: Number(card.reflectDamageRatio || 0),
    isSpell: Boolean(card.isSpell),
    spellEffect: card.spellEffect || null,
    isStructure: Boolean(card.isStructure),
    isModule: Boolean(card.isModule),
    moduleId: card.moduleId || null,
    deployAtBonusIndicator: Boolean(card.deployAtBonusIndicator),
    spawnCount: Number(card.spawnCount || 1),
    capsuleDamage: Number(card.capsuleDamage || 0),
    capsuleSplashRadius: Number(card.capsuleSplashRadius || 0),
    capsuleUnit: card.capsuleUnit || null,
    capsuleLoadCount: Number(card.capsuleLoadCount || 0),
    roles: Array.isArray(hint?.roles) ? [...hint.roles].sort() : [],
    comboTrigger: hint?.comboTrigger || null
  };
  const payload = JSON.stringify(canonical);
  let hash = 0x811c9dc5;
  for (let i = 0; i < payload.length; i++) {
    hash ^= payload.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
}

export const AI_SYNERGY_META = Object.freeze({
  schemaVersion: 1,
  cardHashes: Object.freeze({
    "Marine": "4a78b1fa",
    "Tesla Walker": "106576e4",
    "Devil Patrol": "7db35ed2",
    "Hot Hand": "f4dd3cd4",
    "Pink Beret": "a5d558e9",
    "Hacker": "c93fab58",
    "Wheelie Bomber": "a35804de",
    "Panzer": "9cdfe3f2",
    "Rocketship": "dd196cab",
    "Deployable Cannon": "86dfac2c",
    "Shade": "aed05bc3",
    "A9 Piranha": "f9242a78",
    "A9 Grizzly": "91f2fb91",
    "A9 Pigeon": "98198aa8",
    "Fodder": "bb4b200a",
    "Reconsider": "0ec919d3",
    "Mental Extraction": "13793de3",
    "Invasion Capsule Sling": "010741cf",
    "Drone Command": "3f9d2500",
    "Congress Module": "e44284d5",
    "The Salvage Company": "5d00879a",
    "Orchestra": "34aa3633",
    "Stickman": "e24fec9a",
    "Defective Loaf": "9af63c0b",
    "Rejected Loaf": "44a89e69",
    "Apostate Loaf": "8eb52262",
    "Forgotten Loaf": "20bba363",
    "Delving Pillar": "9b92f037",
    "Harvesting Pillar": "837e3c8b",
    "Processing Pillar": "bebfcef7",
    "Claiming Pillar": "a9a0c309",
    "Preserver Lock": "465c6e86",
    "Sentinal Lock": "a869a671",
    "Bastion Lock": "7adcccb4",
    "Monitor Lock": "1f9a6500",
    "Enforcer Vulture": "a5508b9c",
    "Looting Vulture": "7ed124bb",
    "Parasite Vulture": "981a68b2",
    "Anti Shade": "a73c593d",
    "Forced Overstimulation": "81bb4bbc",
    "Ambush": "4ee4d8b9",
    "Experimental Metamorphosis": "b70a2915",
    "Precision Strike": "fc8b9c75",
    "Retreat Protocol": "155710df",
    "Pulp": "da759752"
})
});

export const AI_SYNERGY_PAIRS = Object.freeze([
  Object.freeze({"cardA":"Fodder","cardB":"Experimental Metamorphosis","optimalDelaySec":2.8,"synergyScore":42.5,"tStat":4.12,"samples":48,"contextTag":"SPAWN_SWARM"}),
  Object.freeze({"cardA":"MODULE:capsule_sling_ready","cardB":"Marine","optimalDelaySec":0,"synergyScore":38,"tStat":3.95,"samples":44,"contextTag":"REQ_ENEMY_AT_FRONTLINE"}),
  Object.freeze({"cardA":"STATE:nuke_imminent","cardB":"Forced Overstimulation","optimalDelaySec":0,"synergyScore":35,"tStat":3.65,"samples":40,"contextTag":"REQ_ENGAGED_SWARM"}),
  Object.freeze({"cardA":"Tesla Walker","cardB":"The Salvage Company","optimalDelaySec":2.8,"synergyScore":26.8,"tStat":3.1,"samples":40,"contextTag":"REQ_STABLE_FRONTLINE"}),
  Object.freeze({"cardA":"Sentinal Lock","cardB":"Pulp","optimalDelaySec":0,"synergyScore":24.5,"tStat":2.98,"samples":40,"contextTag":"ANY"}),
  Object.freeze({"cardA":"Defective Loaf","cardB":"Forgotten Loaf","optimalDelaySec":1.2,"synergyScore":22.1,"tStat":2.85,"samples":40,"contextTag":"SPAWN_SWARM"}),
  Object.freeze({"cardA":"Hot Hand","cardB":"Pink Beret","optimalDelaySec":0,"synergyScore":18.2,"tStat":2.62,"samples":40,"contextTag":"REQ_STABLE_FRONTLINE"}),
  Object.freeze({"cardA":"Marine","cardB":"Tesla Walker","optimalDelaySec":1.2,"synergyScore":16.5,"tStat":2.54,"samples":40,"contextTag":"ANY"})
]);
